import isClient from '@/utils/isClient';
import { mergeStable } from '@/utils/merge';
import { tryGetWindowProp } from '@/utils/tryGetWindowProp';
import useCookies from 'lib/hooks/useCookies';
import { getDomain } from 'lib/utils';
import { getUserLogInUrl, getUserLogOutUrl, getUserPageUrl, getUserSignUpUrl } from 'lib/utils/getUserAuthUrl';
import { usePathname } from 'next/navigation';
import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { useEffectOnce } from 'react-use';

type Urls = {
  logInUrl: string;
  logOutUrl: string;
  signUpUrl: string;
  myPageUrl: string;
};

const getUrls = (state: Urls, returnTo: string) => {
  const params = returnTo ? { return: returnTo } : undefined;
  const alltIdLogOutUrl = isClient() && window.alltId?.getBrandLogoutURL(returnTo);

  const urls = {
    logInUrl: getUserLogInUrl(params),
    logOutUrl: alltIdLogOutUrl || getUserLogOutUrl(params),
    signUpUrl: getUserSignUpUrl(params),
    myPageUrl: getUserPageUrl(params),
  };

  return mergeStable(state, urls);
};

const isValidCookie = (cookie: string | undefined) => Boolean(cookie && cookie.length > 0);

export type UserAuthProps = ReturnType<typeof useUserAuth>;
type AlltId = Window['alltId'];

const useUserAuth = () => {
  const pathname = usePathname();
  const [{ login_token, alltIdData }] = useCookies();
  const [isUserLoggedIn, setUserLoggedIn] = useState(false);
  const [urls, setReturnTo] = useReducer(getUrls, getUrls({} as Urls, ''));
  const [alltId, setAlltId] = useState<AlltId | null>(null);

  useEffect(() => {
    const siteUrl = getDomain(false);
    const isMyPage = pathname.startsWith('/mina-sidor');
    const returnTo = isMyPage ? siteUrl : `${siteUrl}${pathname}`;
    setReturnTo(returnTo);
  }, [pathname, alltId?.config?.jwt]);

  useEffectOnce(() => {
    tryGetWindowProp<AlltId, null>('alltId', { fallbackValue: null }).then(setAlltId);
  });

  useEffect(() => {
    setUserLoggedIn(isValidCookie(login_token) && isValidCookie(alltIdData));
  }, [login_token, alltIdData]);

  const logOut = useCallback(() => {
    window.location.href = urls.logOutUrl;
  }, [urls.logOutUrl]);

  return useMemo(
    () => ({
      isUserLoggedIn,
      logOut,
      ...urls,
    }),
    [isUserLoggedIn, logOut, urls]
  );
};

export default useUserAuth;
